import React, {
  useRef, useLayoutEffect, memo, useMemo, useCallback,
} from 'react';
import { areEqual } from 'react-window';

const THRESHOLD = 2;

const useResizeObserver = (callback) => useMemo(() => {
  if (typeof window === 'undefined' || !('ResizeObserver' in window)) return null;
  return new window.ResizeObserver((entries) => {
    window.requestAnimationFrame(() => callback(entries[0]?.target));
  });
}, [callback]);

const Item = memo(({ index, style, data }) => {
  const itemRef = useRef(null);
  const previousHeight = useRef(null);
  const { height, ...restStyle } = style;
  const {
    items,
    isItemLoaded,
    renderItem,
    setRowHeight,
    itemSize,
  } = data;

  const updateHeight = useCallback((element) => {
    if (!element) return;
    const newHeight = element.getBoundingClientRect().height;
    const shouldUpdate = previousHeight.current !== newHeight && Math.abs(newHeight - itemSize) > THRESHOLD && newHeight > 0;
    if (shouldUpdate) {
      previousHeight.current = newHeight;
      setRowHeight(index, newHeight);
    }
  }, [index, itemSize, setRowHeight]);

  const observer = useResizeObserver(updateHeight);

  useLayoutEffect(() => {
    const element = itemRef.current;
    if (!element || !observer) return () => {};
    observer.observe(element);
    return () => observer.disconnect();
  }, [observer, index, itemSize, setRowHeight]);

  const isLoading = !isItemLoaded(index);
  const item = items[index];

  return (
    <div ref={itemRef} style={{ ...restStyle, height: 'auto' }}>
      {renderItem(item, isLoading, index)}
    </div>
  );
}, areEqual);

export default Item;
